import { AppliedDiscount, appliedDiscountFromApi } from '../applied_discount';
import { AppliedTaxRate } from '../invoice/invoice';
import { BillingStrategy, billingStrategyFromAPI, Currency, currencyFromApi } from '../pricing';
import {
  AppliedCreditInterface,
  AppliedTaxRateInterface,
  CreditType,
  Currency as CurrencyApi,
  PurchaseInterface,
  PurchaseItemInterface,
} from '@vendasta/billing';
import { PurchaseStatus, purchaseStatusFromApi } from './purchase_status';

export interface Purchase {
  merchantId: string;
  timestamp?: Date;
  purchaseId: string;
  total: number;
  currency: Currency;
  renewal: boolean;
  items: PurchaseItem[];
  billingStrategy: BillingStrategy;
  isDunning: boolean;
  dunningAttempt: number;
  pauseDunningUntil?: Date;
  voided?: Date;
  paid: boolean;
  status: PurchaseStatus;
  creditAmount: number;
  appliedCredits: AppliedCredit[];
  taxAmount: number;
  appliedTaxRates: AppliedTaxRate[];
}

export enum AppliedCreditType {
  CREDIT_TYPE_CREDIT_NOTE = 'Credit note',
  CREDIT_TYPE_PROMOTIONAL = 'Promotional',
  CREDIT_TYPE_UNSET = '',
}

export interface AppliedCredit {
  appliedAmount: number;
  referenceId: string;
  referenceType: AppliedCreditType;
}

export interface PurchaseItem {
  sku: string;
  description: string;
  amount: number;
  orderId: string;
  customerId: string;
  discountAmount: number;
  total: number;
  currency?: Currency;
  appliedDiscounts: AppliedDiscount[];
  taxAmount: number;
  appliedTaxRates: AppliedTaxRate[];
  setupFee?: number;
  feeAmount?: number;

  merchantId: string;
  purchaseDay: string;
  purchaseId: string;
}

export function purchaseFromApi(pApi: PurchaseInterface): Purchase {
  return {
    merchantId: pApi.merchantId || '',
    timestamp: pApi.timestamp ? new Date(pApi.timestamp) : undefined,
    purchaseId: pApi.purchaseId || '',
    total: pApi.total || 0,
    currency: pApi.currency ? currencyFromApi(pApi.currency) : Currency.USD,
    renewal: pApi.renewal || false,
    items: pApi.items ? pApi.items.map((p) => purchaseItemFromApi(p)) : [],
    billingStrategy: billingStrategyFromAPI(pApi.billingStrategy),
    isDunning: pApi.isDunning || false,
    dunningAttempt: pApi.dunningAttempt || 0,
    pauseDunningUntil: pApi.pauseDunningUntil ? new Date(pApi.pauseDunningUntil) : undefined,
    voided: pApi.voided ? new Date(pApi.voided) : undefined,
    paid: pApi.paid || false,
    status: purchaseStatusFromApi(pApi.status),
    creditAmount: pApi.creditAmount || 0,
    appliedCredits: pApi.appliedCredits ? pApi.appliedCredits.map((ac) => appliedCreditFromAPI(ac)) : [],
    taxAmount: pApi.taxAmount || 0,
    appliedTaxRates: (pApi.appliedTaxRates || []).map((at) => appliedTaxRateFromAPI(at)),
  };
}

export function purchaseItemFromApi(pApi: PurchaseItemInterface, currencyApi?: CurrencyApi): PurchaseItem {
  let currency;
  if (currencyApi !== undefined) {
    currency = currencyFromApi(currencyApi);
  }
  return {
    sku: pApi.sku || '',
    description: pApi.description || '',
    amount: pApi.amount || 0,
    orderId: pApi.orderId || '',
    customerId: pApi.customerId || '',
    discountAmount: pApi.discountAmount || 0,
    total: pApi.total || 0,
    currency: currency || Currency.USD,
    appliedDiscounts: pApi.appliedDiscounts ? pApi.appliedDiscounts.map((p) => appliedDiscountFromApi(p)) : [],
    merchantId: pApi.merchantId || '',
    purchaseDay: pApi.day || '',
    purchaseId: pApi.purchaseId || '',
    taxAmount: pApi.taxAmount || 0,
    appliedTaxRates: (pApi.appliedTaxRates || []).map((at) => appliedTaxRateFromAPI(at)),
    setupFee: pApi.setupFee || 0,
    feeAmount: pApi.feeAmount || 0,
  };
}

export function appliedCreditFromAPI(ac: AppliedCreditInterface): AppliedCredit {
  return {
    appliedAmount: ac.appliedAmount || 0,
    referenceId: ac.referenceId || '',
    referenceType: appliedCreditTypeFromAPI(ac.referenceType),
  };
}

export function appliedCreditTypeFromAPI(adt: CreditType | undefined): AppliedCreditType {
  switch (adt) {
    case CreditType.CREDIT_TYPE_CREDIT_NOTE:
      return AppliedCreditType.CREDIT_TYPE_CREDIT_NOTE;
    case CreditType.CREDIT_TYPE_PROMOTIONAL:
      return AppliedCreditType.CREDIT_TYPE_PROMOTIONAL;
  }
  return AppliedCreditType.CREDIT_TYPE_UNSET;
}

export function appliedTaxRateFromAPI(at: AppliedTaxRateInterface): AppliedTaxRate {
  return {
    amount: at.amount || 0,
    name: at.name || '',
    percentage: at.percentage || 0,
    taxRateId: at.taxRateId || '',
  };
}
